import { useEffect, useRef, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { Link } from "react-router-dom";
import RightArrow from "../../components/SVG";
import {
  createMember,
  sendVerificationEmail,
  verifyEmailOtp,
  sendMobileOtp,
  verifyMobileOtp,
} from "../../services/memberServices";
import { ArrowLeft, X } from "lucide-react";
import { deleteImage, uploadImages } from "../../services/uploaderServices";
import toast from "react-hot-toast";

const validateRequired = (value) => {
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "boolean") return value === true;
  if (typeof value === "number") return !isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined;
};

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validatePhone = (phone) => {
  const re = /^[0-9]{10,15}$/;
  return re.test(phone);
};

const validateZipCode = (zipcode) => {
  const re = /^[0-9]{5,10}$/;
  return re.test(zipcode);
};

const validateMinMaxSelection = (array, min, max) => {
  return array.length >= min && array.length <= max;
};

const validateImage = (url) => {
  return url !== "";
};

const validateDate = (date) => {
  return date !== "";
};

const validateCheckbox = (isChecked) => {
  return isChecked;
};

const validationRules = {
  profileInfo: {
    firstName: { required: true },
    lastName: { required: true },
    dob: { required: true, validator: validateDate },
    photo: { required: true, validator: validateImage },
    gender: { required: true },
  },
  communication: {
    email: { required: true, validator: validateEmail },
    mobile: { required: true, validator: validatePhone },
    address: { required: true },
    city: { required: true },
    zipcode: { required: true, validator: validateZipCode },
    country: { required: true },
  },
  organization: {
    organizationName: { required: true },
    organizationType: { required: true },
  },
  workExperience: {
    experienceYears: { required: true, validator: (value) => value >= 0 },
  },
  interests: {
    speciesOfInterest: {
      required: true,
      validator: (value) => validateMinMaxSelection(value, 1, 3),
    },
    fieldOfWork: {
      required: true,
      validator: (value) => validateMinMaxSelection(value, 1, 3),
    },
    natureOfWork: {
      required: true,
      validator: (value) => validateMinMaxSelection(value, 1, 3),
    },
  },
  educationDetails: { required: true },
  agreedToTerms: { required: true, validator: validateCheckbox },
};

const validateField = (section, field, value, rules) => {
  if (!rules) return null;
  if (rules.required && !validateRequired(value))
    return "This field is required";
  if (rules.validator && !rules.validator(value)) {
    if (field === "email") return "Invalid email format";
    if (field === "mobile" || field === "telephone" || field === "whatsapp")
      return "Invalid phone number";
    if (field === "zipcode") return "Invalid zipcode";
    if (field === "photo" || field === "companyLogo")
      return "Image is required";
    if (field === "dob") return "Date of birth is required";
    if (field === "speciesOfInterest") return "Please select 1-3 species";
    if (field === "fieldOfWork") return "Please select 1-3 fields";
    if (field === "natureOfWork") return "Please select 1-3 options";
    if (field === "agreedToTerms") return "You must agree to the terms";
    if (field === "experienceYears")
      return "Please enter valid years of experience";
    return "Invalid value";
  }
  return null;
};

const validateForm = (formData) => {
  const errors = {};

  Object.entries(validationRules).forEach(([section, rules]) => {
    if (Array.isArray(rules)) {
      formData[section].forEach((item, index) => {
        Object.entries(rules[index]).forEach(([field, fieldRules]) => {
          const error = validateField(
            `${section}.${index}`,
            field,
            item[field],
            fieldRules,
          );
          if (error) errors[`${section}.${index}.${field}`] = error;
        });
      });
    } else if (typeof rules === "object") {
      Object.entries(rules).forEach(([field, fieldRules]) => {
        const error = validateField(
          section,
          field,
          formData[section][field],
          fieldRules,
        );
        if (error) errors[`${section}.${field}`] = error;
      });
    }
  });

  return errors;
};

const SignUpMain = () => {
  useEffect(() => {
    window.$("select").niceSelect();
  }, []);

  const initialFormData = {
    profileInfo: {
      title: "Mr.",
      firstName: "",
      lastName: "",
      dob: "",
      gender: "Male",
      photo: "",
    },
    communication: {
      email: "",
      telephone: "",
      whatsapp: "",
      mobile: "",
      address: "",
      city: "",
      zipcode: "",
      country: "IN",
      socialLinks: ["", "", ""],
    },
    organization: {
      organizationName: "",
      organizationType: "Private Enterprise",
      organizationAddress: "",
      organizationPhone: "",
      organizationEmail: "",
      aboutCompany: "",
      companyLogo: "",
    },
    workExperience: {
      experienceYears: 0,
      experienceMonths: 0,
    },
    interests: {
      speciesOfInterest: [],
      fieldOfWork: [],
      natureOfWork: [],
      speciesOther: "",
      fieldOther: "",
      natureOther: "",
    },
    educationDetails: "",
    recommendations: [
      {
        name: "",
        membershipNumber: "",
        companyName: "",
        mobile: "",
        email: "",
      },
      {
        name: "",
        membershipNumber: "",
        companyName: "",
        mobile: "",
        email: "",
      },
    ],
    // NEW optional field
    membershipId: "",

    memberRole: "Lifetime Member",
    memberDesignation: "6896f0a379b374d7da63d536",
    year: new Date().getFullYear().toString(),
    agreedToTerms: false,
    isEmailVerified: true,
    isMobileVerified: true,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileOne, setFileOne] = useState("No file chosen");
  const [fileTwo, setFileTwo] = useState("No file chosen");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [othersState, setOthersState] = useState({
    speciesOfInterest: false,
    fieldOfWork: false,
    natureOfWork: false,
  });

  console.log(formData, "formData");

  const fileOneRef = useRef(null);
  const fileTwoRef = useRef(null);

  const handleChange = (e, section, field) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    if (errors[`${section}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${section}.${field}`]: undefined }));
    }

    // Reset verification status when email/mobile changes
    if (section === "communication") {
      if (field === "email") {
        setFormData((prev) => ({ ...prev, isEmailVerified: false }));
      }
      if (field === "mobile") {
        setFormData((prev) => ({ ...prev, isMobileVerified: false }));
      }
    }
  };

  // NEW: simple handler for top-level fields (membershipId etc.)
  const handleRootChange = (e, field) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayChange = (e, section, field, index) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) =>
          i === index ? value : item,
        ),
      },
    }));
  };

  // Fixed checkbox handler that prevents duplicates and handles "Others" properly
  const handleCheckboxChange = (e, section, field, value) => {
    const { checked } = e.target;

    if (value === "Others") {
      // Handle "Others" checkbox separately
      setOthersState((prev) => ({
        ...prev,
        [field]: checked,
      }));

      const otherField =
        field === "speciesOfInterest"
          ? "speciesOther"
          : field === "fieldOfWork"
            ? "fieldOther"
            : "natureOther";

      if (!checked) {
        // Clear the text field when "Others" is unchecked
        setFormData((prev) => ({
          ...prev,
          interests: {
            ...prev.interests,
            [otherField]: "",
          },
        }));
      }
    } else {
      // Handle regular checkboxes - prevent duplicates
      setFormData((prev) => {
        const currentArray = prev[section][field];
        let updatedArray;

        if (checked) {
          // Only add if not already present
          updatedArray = currentArray.includes(value)
            ? currentArray
            : [...currentArray, value];
        } else {
          updatedArray = currentArray.filter((item) => item !== value);
        }

        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: updatedArray,
          },
        };
      });
    }

    if (errors[`${section}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${section}.${field}`]: undefined }));
    }
  };

  // Handle text input for "Others" fields
  const handleOtherInputChange = (value, otherField) => {
    setFormData((prev) => ({
      ...prev,
      interests: {
        ...prev.interests,
        [otherField]: value,
      },
    }));
  };

  // Function to get cleaned data for submission (removes "Others" and combines with custom text)
  const getCleanedInterestsData = () => {
    const cleanedInterests = {
      speciesOfInterest: [
        ...formData.interests.speciesOfInterest, // Already doesn't contain "Others"
        ...(othersState.speciesOfInterest &&
        formData.interests.speciesOther.trim()
          ? formData.interests.speciesOther
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item)
          : []),
      ].filter((item, index, self) => self.indexOf(item) === index), // Remove duplicates

      fieldOfWork: [
        ...formData.interests.fieldOfWork, // Already doesn't contain "Others"
        ...(othersState.fieldOfWork && formData.interests.fieldOther.trim()
          ? formData.interests.fieldOther
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item)
          : []),
      ].filter((item, index, self) => self.indexOf(item) === index), // Remove duplicates

      natureOfWork: [
        ...formData.interests.natureOfWork, // Already doesn't contain "Others"
        ...(othersState.natureOfWork && formData.interests.natureOther.trim()
          ? formData.interests.natureOther
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item)
          : []),
      ].filter((item, index, self) => self.indexOf(item) === index), // Remove duplicates
    };

    return cleanedInterests;
  };

  const handleRecommendationChange = (e, index, field) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      recommendations: prev.recommendations.map((rec, i) =>
        i === index ? { ...rec, [field]: value } : rec,
      ),
    }));
    if (errors[`recommendations.${index}.${field}`]) {
      setErrors((prev) => ({
        ...prev,
        [`recommendations.${index}.${field}`]: undefined,
      }));
    }
  };

  const handleEducationDetailsChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, educationDetails: value }));
    if (errors.educationDetails) {
      setErrors((prev) => ({ ...prev, educationDetails: undefined }));
    }
  };

  const handleTermsChange = (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({ ...prev, agreedToTerms: checked }));
    if (errors.agreedToTerms) {
      setErrors((prev) => ({ ...prev, agreedToTerms: undefined }));
    }
  };

  const handleFileOneChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileOne("Uploading...");
    try {
      const formDataLocal = new FormData();
      formDataLocal.append("images", file);
      const response = await uploadImages(formDataLocal);
      const imageUrl = response.images[0].imageUrl;
      setFileOne(file.name);
      setFormData((prev) => ({
        ...prev,
        profileInfo: { ...prev.profileInfo, photo: imageUrl },
      }));
      if (errors["profileInfo.photo"]) {
        setErrors((prev) => ({ ...prev, "profileInfo.photo": undefined }));
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      setFileOne("Upload failed");
    } finally {
      if (fileOneRef.current) fileOneRef.current.value = "";
    }
  };

  const handleDeletePhoto = async () => {
    try {
      if (formData.profileInfo.photo) {
        await deleteImage(formData.profileInfo.photo);
      }
      setFileOne("No file chosen");
      setFormData((prev) => ({
        ...prev,
        profileInfo: { ...prev.profileInfo, photo: "" },
      }));
    } catch (error) {
      console.error("Error deleting photo:", error);
    } finally {
      if (fileOneRef.current) fileOneRef.current.value = "";
    }
  };

  const handleFileTwoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileTwo("Uploading...");
    try {
      const formDataLocal = new FormData();
      formDataLocal.append("images", file);
      const response = await uploadImages(formDataLocal);
      const imageUrl = response.images[0].imageUrl;
      setFileTwo(file.name);
      setFormData((prev) => ({
        ...prev,
        organization: { ...prev.organization, companyLogo: imageUrl },
      }));
      if (errors["organization.companyLogo"]) {
        setErrors((prev) => ({
          ...prev,
          "organization.companyLogo": undefined,
        }));
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      setFileTwo("Upload failed");
    } finally {
      if (fileTwoRef.current) fileTwoRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    try {
      if (formData.organization.companyLogo) {
        await deleteImage(formData.organization.companyLogo);
      }
      setFileTwo("No file chosen");
      setFormData((prev) => ({
        ...prev,
        organization: { ...prev.organization, companyLogo: "" },
      }));
    } catch (error) {
      console.error("Error deleting logo:", error);
    } finally {
      if (fileTwoRef.current) fileTwoRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create validation data with cleaned interests
    const cleanedInterests = getCleanedInterestsData();
    const validationData = {
      ...formData,
      interests: cleanedInterests,
    };

    const validationErrors = validateForm(validationData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstErrorKey = Object.keys(validationErrors)[0];
      const firstErrorElement = document.querySelector(
        `[name="${firstErrorKey}"]`,
      );
      firstErrorElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        interests: cleanedInterests,
        profileInfo: {
          ...formData.profileInfo,
          dob: formData.profileInfo.dob
            ? new Date(formData.profileInfo.dob)
            : null,
        },
        membershipType: "standard",
        membershipId: formData.membershipId?.trim()
          ? formData.membershipId.trim()
          : null,
        memberRole: "Lifetime Member",
        memberDesignation: "6896f0a379b374d7da63d536",
        status: "pending",
      };

      delete payload.interests.speciesOther;
      delete payload.interests.fieldOther;
      delete payload.interests.natureOther;

      await createMember(payload);
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      if (error.response?.data?.details) {
        const validationErrors = {};
        error.response.data.details.forEach((detail) => {
          validationErrors[detail.path.join(".")] = detail.message;
        });
        setErrors(validationErrors);
      } else {
        toast.success(
          error?.response?.data?.message || "Failed to submit application",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <main>
        <Breadcrumb title="LIFE MEMBERSHIP APPLICATION" />
        <div className="it-student-area it-instructor-style">
          <div className="container">
            <div className="row">
              <div className="col-xl-12">
                <div className="it-student-bg text-center p-5">
                  <h3 className="text-success mb-4">
                    Application Submitted Successfully!
                  </h3>
                  <p>
                    Thank you for your application. We will review your details
                    and get back to you soon.
                  </p>
                  <Link
                    to="/"
                    className="bg-[#704FE6] mx-auto text-white w-[200px] h-[48px] rounded-lg flex justify-center items-center"
                  >
                    <ArrowLeft className="me-2" size={16} />
                    <span>Return to Home</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }
  return (
    <main>
      <Breadcrumb title="LIFE MEMBERSHIP APPLICATION" />
      <div className="it-student-area it-instructor-style">
        <div className="container">
          <div className="row">
            <div className="col-xl-12">
              <div className="it-student-bg">
                <p>
                  <span className="text-danger">*</span>Life Membership offers a
                  one-time enrollment that grants lifetime access to SAP's
                  resources and benefits.
                </p>
                <div className="it-signup-text mb-20">
                  <span>
                    You have an account yet? <Link to="/login">Sign In</Link>
                  </span>
                </div>
                <div>
                  {/* Profile Information Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Profile information
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-2">
                          <div className="it-student-regiform-item">
                            <label>Title</label>
                            <div className="postbox__select">
                              <select
                                value={formData.profileInfo.title}
                                onChange={(e) =>
                                  handleChange(e, "profileInfo", "title")
                                }
                              >
                                <option>Mr.</option>
                                <option>Ms.</option>
                                <option>Dr.</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>First Name *</label>
                            <input
                              type="text"
                              name="profileInfo.firstName"
                              value={formData.profileInfo.firstName}
                              onChange={(e) =>
                                handleChange(e, "profileInfo", "firstName")
                              }
                            />
                            {errors["profileInfo.firstName"] && (
                              <div className="text-danger">
                                {errors["profileInfo.firstName"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-3">
                          <div className="it-student-regiform-item">
                            <label>Last Name *</label>
                            <input
                              type="text"
                              name="profileInfo.lastName"
                              value={formData.profileInfo.lastName}
                              onChange={(e) =>
                                handleChange(e, "profileInfo", "lastName")
                              }
                            />
                            {errors["profileInfo.lastName"] && (
                              <div className="text-danger">
                                {errors["profileInfo.lastName"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Date of Birth *</label>
                            <input
                              type="date"
                              name="profileInfo.dob"
                              value={formData.profileInfo.dob}
                              onChange={(e) =>
                                handleChange(e, "profileInfo", "dob")
                              }
                            />
                            {errors["profileInfo.dob"] && (
                              <div className="text-danger">
                                {errors["profileInfo.dob"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Gender *</label>
                            <div className="postbox__select">
                              <select
                                value={formData.profileInfo.gender}
                                onChange={(e) =>
                                  handleChange(e, "profileInfo", "gender")
                                }
                              >
                                <option>Male</option>
                                <option>Female</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Photo *</label>
                            <div className="postbox__resume-wrap mb-20 d-flex align-items-center flex-wrap">
                              <div className="postbox__resume mr-20">
                                <input
                                  id="fileOne"
                                  type="file"
                                  hidden
                                  ref={fileOneRef}
                                  accept="image/*"
                                  onChange={handleFileOneChange}
                                />
                                <label htmlFor="fileOne">
                                  <span className="text-white">
                                    Choose File
                                  </span>
                                </label>
                              </div>
                              <div
                                className="postbox__resume border-transparent text-black"
                                style={{ backgroundColor: "transparent" }}
                              >
                                <label htmlFor="fileOne">
                                  <span>{fileOne}</span>
                                </label>
                              </div>
                            </div>
                            {formData.profileInfo.photo && (
                              <div
                                className="mb-2 position-relative"
                                style={{ width: "100px", height: "100px" }}
                              >
                                <img
                                  src={formData.profileInfo.photo}
                                  alt="Preview"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                                <button
                                  onClick={handleDeletePhoto}
                                  className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    transform: "translate(50%, -50%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title="Remove photo"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            )}
                            {errors["profileInfo.photo"] && (
                              <div className="text-danger">
                                {errors["profileInfo.photo"]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Communication Details Section with Verification */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Communication Details
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Email ID *</label>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                name="communication.email"
                                value={formData.communication.email}
                                onChange={(e) =>
                                  handleChange(e, "communication", "email")
                                }
                                style={{
                                  backgroundColor: formData.isEmailVerified
                                    ? "#f8f9fa"
                                    : "white",
                                }}
                              />
                            </div>
                            {errors["communication.email"] && (
                              <div className="text-danger">
                                {errors["communication.email"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Mobile *</label>
                            <div className="d-flex align-items-center gap-2">
                              <div className="position-relative flex-grow-1">
                                <div
                                  className="position-absolute d-flex align-items-center"
                                  style={{
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    height: "30px",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                  }}
                                >
                                  <span
                                    style={{
                                      color: "#666",
                                      fontSize: "16px",
                                    }}
                                  >
                                    +91
                                  </span>
                                  <div
                                    style={{
                                      width: "1px",
                                      height: "30px",
                                      backgroundColor: "grey",
                                      marginLeft: "10px",
                                      marginRight: "10px",
                                    }}
                                  ></div>
                                </div>
                                <input
                                  type="text"
                                  name="communication.mobile"
                                  value={formData.communication.mobile}
                                  onChange={(e) => {
                                    // Only allow digits, prevent leading zero, max 10 digits
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    ); // Remove non-digits
                                    if (
                                      value.length === 0 ||
                                      (value[0] !== "0" && value.length <= 10)
                                    ) {
                                      handleChange(
                                        {
                                          ...e,
                                          target: { ...e.target, value: value },
                                        },
                                        "communication",
                                        "mobile",
                                      );
                                    }
                                  }}
                                  placeholder="Enter 10-digit mobile number"
                                  maxLength={10}
                                  style={{
                                    backgroundColor: "white",
                                    paddingLeft: "65px", // Increased padding to accommodate +91 and vertical line
                                    height: "58px",
                                  }}
                                  className="form-control"
                                />
                              </div>
                            </div>
                            {errors["communication.mobile"] && (
                              <div className="text-danger">
                                {errors["communication.mobile"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Telephone</label>
                            <input
                              type="text"
                              name="communication.telephone"
                              value={formData.communication.telephone}
                              onChange={(e) =>
                                handleChange(e, "communication", "telephone")
                              }
                              maxLength={10} // restrict to 10 digits
                              pattern="\d{10}" // enforce exactly 10 digits on submit
                              title="Telephone number must be exactly 10 digits"
                              onInput={(e) => {
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(/\D/g, ""); // allow only digits
                              }}
                            />
                            {errors["communication.telephone"] && (
                              <div className="text-danger">
                                {errors["communication.telephone"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>WhatsApp Number</label>
                            <input
                              type="text"
                              name="communication.whatsapp"
                              value={formData.communication.whatsapp}
                              onChange={(e) =>
                                handleChange(e, "communication", "whatsapp")
                              }
                              maxLength={10}
                              pattern="\d{10}"
                              title="WhatsApp number must be exactly 10 digits"
                              onInput={(e) => {
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(/\D/g, "");
                              }}
                            />
                            {errors["communication.whatsapp"] && (
                              <div className="text-danger">
                                {errors["communication.whatsapp"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Address *</label>
                            <input
                              type="text"
                              name="communication.address"
                              value={formData.communication.address}
                              onChange={(e) =>
                                handleChange(e, "communication", "address")
                              }
                            />
                            {errors["communication.address"] && (
                              <div className="text-danger">
                                {errors["communication.address"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>City *</label>
                            <input
                              type="text"
                              name="communication.city"
                              value={formData.communication.city}
                              onChange={(e) =>
                                handleChange(e, "communication", "city")
                              }
                            />
                            {errors["communication.city"] && (
                              <div className="text-danger">
                                {errors["communication.city"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Zipcode *</label>
                            <input
                              type="text"
                              name="communication.zipcode"
                              value={formData.communication.zipcode}
                              onChange={(e) =>
                                handleChange(e, "communication", "zipcode")
                              }
                            />
                            {errors["communication.zipcode"] && (
                              <div className="text-danger">
                                {errors["communication.zipcode"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Country *</label>
                            <div className="postbox__select">
                              <select
                                name="communication.country"
                                value={formData.communication.country}
                                onChange={(e) =>
                                  handleChange(e, "communication", "country")
                                }
                              >
                                <option value="IN">India</option>
                                <option value="AF">Afghanistan</option>
                                <option value="AR">Argentina</option>
                                <option value="AU">Australia</option>
                                <option value="BD">Bangladesh</option>
                                <option value="BR">Brazil</option>
                                <option value="CA">Canada</option>
                                <option value="CN">China</option>
                                <option value="EG">Egypt</option>
                                <option value="FR">France</option>
                                <option value="DE">Germany</option>
                                <option value="ID">Indonesia</option>
                                <option value="IR">Iran</option>
                                <option value="IQ">Iraq</option>
                                <option value="IT">Italy</option>
                                <option value="JP">Japan</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Website/Social Media</label>
                            <input
                              type="text"
                              value={formData.communication.socialLinks[0]}
                              onChange={(e) =>
                                handleArrayChange(
                                  e,
                                  "communication",
                                  "socialLinks",
                                  0,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Website/Social Media</label>
                            <input
                              type="text"
                              value={formData.communication.socialLinks[1]}
                              onChange={(e) =>
                                handleArrayChange(
                                  e,
                                  "communication",
                                  "socialLinks",
                                  1,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Website/Social Media</label>
                            <input
                              type="text"
                              value={formData.communication.socialLinks[2]}
                              onChange={(e) =>
                                handleArrayChange(
                                  e,
                                  "communication",
                                  "socialLinks",
                                  2,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Organizational Details Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Organizational Details
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Organization Name *</label>
                            <input
                              type="text"
                              name="organization.organizationName"
                              value={formData.organization.organizationName}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "organization",
                                  "organizationName",
                                )
                              }
                            />
                            {errors["organization.organizationName"] && (
                              <div className="text-danger">
                                {errors["organization.organizationName"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Organization Type *</label>
                            <div className="postbox__select">
                              <select
                                name="organization.organizationType"
                                value={formData.organization.organizationType}
                                onChange={(e) =>
                                  handleChange(
                                    e,
                                    "organization",
                                    "organizationType",
                                  )
                                }
                              >
                                <option value="Private Enterprise">
                                  Private Enterprise
                                </option>
                                <option value="Educational Institution">
                                  Educational Institution
                                </option>
                                <option value="Research Organization">
                                  Research Organization
                                </option>
                                <option value="Government">Government</option>
                                <option value="Others">Others</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Address</label>
                            <textarea
                              style={{
                                border: "1px solid black",
                                borderRadius: "10px",
                                height: "180px",
                              }}
                              value={formData.organization.organizationAddress}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "organization",
                                  "organizationAddress",
                                )
                              }
                            ></textarea>
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Phone</label>
                            <input
                              type="text"
                              name="organization.organizationPhone"
                              value={formData.organization.organizationPhone}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "organization",
                                  "organizationPhone",
                                )
                              }
                              maxLength={10} // restrict to 10 digits
                              pattern="\d{10}" // enforce exactly 10 digits on submit
                              title="Phone number must be exactly 10 digits"
                              onInput={(e) => {
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(/\D/g, ""); // allow only digits
                              }}
                            />
                            {errors["organization.organizationPhone"] && (
                              <div className="text-danger">
                                {errors["organization.organizationPhone"]}
                              </div>
                            )}
                          </div>

                          <div className="it-student-regiform-item">
                            <label>Email</label>
                            <input
                              type="text"
                              name="organization.organizationEmail"
                              value={formData.organization.organizationEmail}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "organization",
                                  "organizationEmail",
                                )
                              }
                            />
                            {errors["organization.organizationEmail"] && (
                              <div className="text-danger">
                                {errors["organization.organizationEmail"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-12">
                          <div className="it-student-regiform-item">
                            <label>About Company</label>
                            <textarea
                              style={{
                                border: "1px solid black",
                                borderRadius: "10px",
                                height: "180px",
                              }}
                              value={formData.organization.aboutCompany}
                              onChange={(e) =>
                                handleChange(e, "organization", "aboutCompany")
                              }
                            ></textarea>
                          </div>
                        </div>
                        <div className="col-xl-12">
                          <div className="it-student-regiform-item">
                            <label>Company Logo</label>
                            <div className="postbox__resume-wrap mb-20 d-flex align-items-center flex-wrap">
                              <div className="postbox__resume mr-20">
                                <input
                                  id="fileTwo"
                                  type="file"
                                  ref={fileTwoRef}
                                  hidden
                                  accept="image/*"
                                  onChange={handleFileTwoChange}
                                />
                                <label htmlFor="fileTwo">
                                  <span className="text-white">
                                    Choose File
                                  </span>
                                </label>
                              </div>
                              <div
                                className="postbox__resume border-transparent text-black"
                                style={{ backgroundColor: "transparent" }}
                              >
                                <label htmlFor="fileTwo">
                                  <span>{fileTwo}</span>
                                </label>
                              </div>
                            </div>
                            {formData.organization.companyLogo && (
                              <div
                                className="mb-2 position-relative"
                                style={{ width: "100px", height: "100px" }}
                              >
                                <img
                                  src={formData.organization.companyLogo}
                                  alt="Company Logo Preview"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                                <button
                                  onClick={handleDeleteLogo}
                                  className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    transform: "translate(50%, -50%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title="Remove logo"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            )}
                            {errors["organization.companyLogo"] && (
                              <div className="text-danger">
                                {errors["organization.companyLogo"]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Work Experience Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Work Experience
                    </h4>
                    <p>
                      Provide details such as organization name, your job title
                      and/or responsibilities, and number of years and months in
                      each job in the following. Please start with your current
                      position.
                    </p>
                    <h6>Year of Start in Work Experience in Aquaculture</h6>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Years *</label>
                            <input
                              type="number"
                              name="workExperience.experienceYears"
                              min="0"
                              value={formData.workExperience.experienceYears}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "workExperience",
                                  "experienceYears",
                                )
                              }
                            />
                            {errors["workExperience.experienceYears"] && (
                              <div className="text-danger">
                                {errors["workExperience.experienceYears"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Months (Optional)</label>
                            <input
                              type="number"
                              min="0"
                              max="11"
                              value={formData.workExperience.experienceMonths}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  "workExperience",
                                  "experienceMonths",
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Species of Interest Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Species of Interest
                    </h4>
                    <p>
                      (Required Minimum one and maximum 3 areas can be selected)
                      *
                    </p>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-8">
                          <div className="row mb-20">
                            <div className="col-xl-6">
                              <div className="it-signup-agree">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.speciesOfInterest"
                                    checked={formData.interests.speciesOfInterest.includes(
                                      "Shrimp",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "speciesOfInterest",
                                        "Shrimp",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Shrimp
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.speciesOfInterest"
                                    checked={formData.interests.speciesOfInterest.includes(
                                      "Warm Water Freshwater Fish",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "speciesOfInterest",
                                        "Warm Water Freshwater Fish",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Warm Water Freshwater Fish
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-3">
                              <div className="it-signup-agree">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.speciesOfInterest"
                                    checked={formData.interests.speciesOfInterest.includes(
                                      "Coldwater Fish",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "speciesOfInterest",
                                        "Coldwater Fish",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Coldwater Fish
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.speciesOfInterest"
                                    checked={formData.interests.speciesOfInterest.includes(
                                      "Marine Fish",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "speciesOfInterest",
                                        "Marine Fish",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Marine Fish
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-3">
                              <div className="it-instructor-wrap">
                                <div className="it-signup-agree">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      name="interests.speciesOfInterest"
                                      checked={othersState.speciesOfInterest}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          e,
                                          "interests",
                                          "speciesOfInterest",
                                          "Others",
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="flexCheckDefault"
                                    >
                                      Others
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-xl-6">
                              <div className="it-student-regiform-item">
                                <input
                                  type="text"
                                  placeholder="Others (comma separated)"
                                  value={formData.interests.speciesOther}
                                  onChange={(e) =>
                                    handleOtherInputChange(
                                      e.target.value,
                                      "speciesOther",
                                    )
                                  }
                                  disabled={!othersState.speciesOfInterest}
                                />
                              </div>
                            </div>
                          </div>
                          {errors["interests.speciesOfInterest"] && (
                            <div className="text-danger">
                              {errors["interests.speciesOfInterest"]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Field of Work Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">Field of Work</h4>
                    <p>
                      (Required Minimum one and maximum 3 areas can be selected)
                      *
                    </p>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-8">
                          <div className="row mb-20">
                            <div className="col-xl-6">
                              <div className="it-signup-agree">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Hatchery Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Hatchery Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Hatchery Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Farm Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Farm Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Farm Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Healthcare Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Healthcare Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Healthcare Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Feed Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Feed Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Feed Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Machinery Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Machinery Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Machinery Sector
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-6">
                              <div className="it-signup-agree">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Government Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Government Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Government Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Processing Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Processing Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Processing Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Fish Farming & Allied Sector",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Fish Farming & Allied Sector",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Fish Farming & Allied Sector
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Markets, Certifications, Policy",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Markets, Certifications, Policy",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Markets, Certifications, Policy
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={formData.interests.fieldOfWork.includes(
                                      "Media",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Media",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Media
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.fieldOfWork"
                                    checked={othersState.fieldOfWork}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "fieldOfWork",
                                        "Others",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Others
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-xl-6">
                              <div className="it-student-regiform-item">
                                <input
                                  type="text"
                                  placeholder="Others (comma separated)"
                                  value={formData.interests.fieldOther}
                                  onChange={(e) =>
                                    handleOtherInputChange(
                                      e.target.value,
                                      "fieldOther",
                                    )
                                  }
                                  disabled={!othersState.fieldOfWork}
                                />
                              </div>
                            </div>
                          </div>
                          {errors["interests.fieldOfWork"] && (
                            <div className="text-danger">
                              {errors["interests.fieldOfWork"]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Nature of Work Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Nature of Work
                    </h4>
                    <p>
                      (Required Minimum one and maximum 3 areas can be selected)
                      *
                    </p>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-8">
                          <div className="row mb-20">
                            <div className="col-xl-6">
                              <div className="it-signup-agree">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.natureOfWork"
                                    checked={formData.interests.natureOfWork.includes(
                                      "Teaching",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "natureOfWork",
                                        "Teaching",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Teaching
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.natureOfWork"
                                    checked={formData.interests.natureOfWork.includes(
                                      "Research",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "natureOfWork",
                                        "Research",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Research
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.natureOfWork"
                                    checked={formData.interests.natureOfWork.includes(
                                      "Production",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "natureOfWork",
                                        "Production",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Production
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-3">
                              <div className="it-signup-agree">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.natureOfWork"
                                    checked={formData.interests.natureOfWork.includes(
                                      "Management",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "natureOfWork",
                                        "Management",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Management
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.natureOfWork"
                                    checked={formData.interests.natureOfWork.includes(
                                      "Policy",
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "natureOfWork",
                                        "Policy",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Policy
                                  </label>
                                </div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="interests.natureOfWork"
                                    checked={othersState.natureOfWork}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        "interests",
                                        "natureOfWork",
                                        "Others",
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="flexCheckDefault"
                                  >
                                    Others
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-xl-6">
                              <div className="it-student-regiform-item">
                                <input
                                  type="text"
                                  placeholder="Others (comma separated)"
                                  value={formData.interests.natureOther}
                                  onChange={(e) =>
                                    handleOtherInputChange(
                                      e.target.value,
                                      "natureOther",
                                    )
                                  }
                                  disabled={!othersState.natureOfWork}
                                />
                              </div>
                            </div>
                          </div>
                          {errors["interests.natureOfWork"] && (
                            <div className="text-danger">
                              {errors["interests.natureOfWork"]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Education Details Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Education Details
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-12">
                          <div className="it-student-regiform-item">
                            <textarea
                              name="educationDetails"
                              style={{
                                border: "1px solid black",
                                borderRadius: "10px",
                                height: "180px",
                              }}
                              value={formData.educationDetails}
                              onChange={handleEducationDetailsChange}
                            ></textarea>
                            {errors.educationDetails && (
                              <div className="text-danger">
                                {errors.educationDetails}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* NEW: Membership ID (optional)*/}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Membership ID (if any)
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Membership ID (optional)</label>
                            <input
                              type="text"
                              name="membershipId"
                              value={formData.membershipId}
                              onChange={(e) =>
                                handleRootChange(e, "membershipId")
                              }
                              placeholder="Enter membership Id"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recommendation for Membership Section */}
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Recommendation for Membership
                    </h4>
                    <p>
                      Each application for membership should be supported by two
                      members of Society of Aquaculture Professionals. Please
                      fill up the following
                    </p>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <h6>Recommendation 1</h6>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Name *</label>
                            <input
                              type="text"
                              name="recommendations.0.name"
                              value={formData.recommendations[0].name}
                              onChange={(e) =>
                                handleRecommendationChange(e, 0, "name")
                              }
                            />
                            {errors["recommendations.0.name"] && (
                              <div className="text-danger">
                                {errors["recommendations.0.name"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Membership Number *</label>
                            <input
                              type="text"
                              name="recommendations.0.membershipNumber"
                              value={
                                formData.recommendations[0].membershipNumber
                              }
                              onChange={(e) =>
                                handleRecommendationChange(
                                  e,
                                  0,
                                  "membershipNumber",
                                )
                              }
                            />
                            {errors["recommendations.0.membershipNumber"] && (
                              <div className="text-danger">
                                {errors["recommendations.0.membershipNumber"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Company Name *</label>
                            <input
                              type="text"
                              name="recommendations.0.companyName"
                              value={formData.recommendations[0].companyName}
                              onChange={(e) =>
                                handleRecommendationChange(e, 0, "companyName")
                              }
                            />
                            {errors["recommendations.0.companyName"] && (
                              <div className="text-danger">
                                {errors["recommendations.0.companyName"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Mobile Number *</label>
                            <input
                              type="text"
                              name="recommendations.0.mobile"
                              value={formData.recommendations[0].mobile}
                              onChange={(e) =>
                                handleRecommendationChange(e, 0, "mobile")
                              }
                              maxLength={10} // limit to 10 digits
                              pattern="\d{10}" // enforce 10-digit numbers on form submit
                              title="Mobile number must be exactly 10 digits"
                              onInput={(e) => {
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(/\D/g, ""); // only digits
                              }}
                            />
                            {errors["recommendations.0.mobile"] && (
                              <div className="text-danger">
                                {errors["recommendations.0.mobile"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Email ID *</label>
                            <input
                              type="text"
                              name="recommendations.0.email"
                              value={formData.recommendations[0].email}
                              onChange={(e) =>
                                handleRecommendationChange(e, 0, "email")
                              }
                            />
                            {errors["recommendations.0.email"] && (
                              <div className="text-danger">
                                {errors["recommendations.0.email"]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <h6>Recommendation 2</h6>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Name *</label>
                            <input
                              type="text"
                              name="recommendations.1.name"
                              value={formData.recommendations[1].name}
                              onChange={(e) =>
                                handleRecommendationChange(e, 1, "name")
                              }
                            />
                            {errors["recommendations.1.name"] && (
                              <div className="text-danger">
                                {errors["recommendations.1.name"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Membership Number *</label>
                            <input
                              type="text"
                              name="recommendations.1.membershipNumber"
                              value={
                                formData.recommendations[1].membershipNumber
                              }
                              onChange={(e) =>
                                handleRecommendationChange(
                                  e,
                                  1,
                                  "membershipNumber",
                                )
                              }
                            />
                            {errors["recommendations.1.membershipNumber"] && (
                              <div className="text-danger">
                                {errors["recommendations.1.membershipNumber"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Company Name *</label>
                            <input
                              type="text"
                              name="recommendations.1.companyName"
                              value={formData.recommendations[1].companyName}
                              onChange={(e) =>
                                handleRecommendationChange(e, 1, "companyName")
                              }
                            />
                            {errors["recommendations.1.companyName"] && (
                              <div className="text-danger">
                                {errors["recommendations.1.companyName"]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Mobile Number *</label>
                            <input
                              type="text"
                              name="recommendations.1.mobile"
                              value={formData.recommendations[1].mobile}
                              onChange={(e) =>
                                handleRecommendationChange(e, 1, "mobile")
                              }
                              maxLength={10} // limit to 10 characters
                              pattern="\d{10}" // only digits, exactly 10
                              title="Mobile number must be exactly 10 digits" // browser shows tooltip
                              onInput={(e) => {
                                // allow only digits
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(/\D/g, "");
                              }}
                            />
                            {errors["recommendations.1.mobile"] && (
                              <div className="text-danger">
                                {errors["recommendations.1.mobile"]}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-4">
                          <div className="it-student-regiform-item">
                            <label>Email ID *</label>
                            <input
                              type="text"
                              name="recommendations.1.email"
                              value={formData.recommendations[1].email}
                              onChange={(e) =>
                                handleRecommendationChange(e, 1, "email")
                              }
                            />
                            {errors["recommendations.1.email"] && (
                              <div className="text-danger">
                                {errors["recommendations.1.email"]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Terms and Submit Section */}
                      <div className="col-xl-12">
                        <div className="it-instructor-wrap">
                          <div className="it-signup-agree">
                            <p>
                              Please enroll me as Life Member of Society of
                              Aquaculture Professional.
                            </p>
                            <div className="form-check d-flex">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="agreedToTerms"
                                checked={formData.agreedToTerms}
                                onChange={handleTermsChange}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="flexCheckDefault"
                              >
                                I confirm that the information provided is true
                                and complete to the best of my knowledge
                              </label>
                            </div>
                            {errors["agreedToTerms"] && (
                              <div className="text-danger">
                                {errors["agreedToTerms"]}
                              </div>
                            )}
                            <p>
                              I will abide to rules, regulations and decisions
                              of the Governing body of Society of Aquaculture
                              Professionals. I am remitting Rs. 11,800 using a
                              secure online payment method. I understand that in
                              case my application is not accepted for
                              membership, the amount will be fully refunded to
                              me by the Society of Aquaculture Professionals.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button with Verification Check */}
                      <div className="it-student-regiform-btn">
                        <button
                          onClick={handleSubmit}
                          className="ed-btn-theme"
                          type="button"
                          disabled={isSubmitting}
                        >
                          <span>
                            {isSubmitting ? "Submitting..." : "Register Now"}
                            <i>
                              <RightArrow />
                            </i>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignUpMain;
